/**
 * Reach audio data (M3 plan R11; audio-direction §3, §9): `reach-audio.json` validated with Zod.
 * - `cruise`: the cruise hum loop (`LOOP.reachCruise`). Gain, playback rate and low-pass follow the travel speed on
 *   a log scale from `speedMin` to `speedMax` (1 km/s to the 2×10¹⁰ m/s cap, D-TBD 2); lanes scale the gain.
 * - `events`: reach event → sound. `route` is `hull` (the player's own ship, through the hull chain), `ui` (cockpit
 *   tones and alarms) or `world` (at the event's float64 world position, falling back to the cockpit when the event
 *   has no position or is too far to hear). `a` / `b` restrict the mapping to events with that field value.
 * The parsed object is mutable so the dev panel can tune the cruise curve live.
 */
import { z } from 'zod'
import { REV, type ReachEventName } from '../../reach/events'
import { SOUND_IDS } from '../sfxSchema'
import reachAudioJson from './reach-audio.json'

export const REACH_ROUTES = ['hull', 'ui', 'world'] as const
export type ReachRoute = (typeof REACH_ROUTES)[number]

const positive = z.number().positive()

export const reachEventSoundSchema = z.object({
  sound: z.enum(SOUND_IDS),
  route: z.enum(REACH_ROUTES),
  gain: z.number().min(0).max(4).default(1),
  /** Play only when the event's `a` (or `b`) field equals this (e.g. `cruiseCharge` a = 0: a start, not a cancel). */
  a: z.number().int().optional(),
  b: z.number().int().optional(),
})

const eventNames = Object.keys(REV) as [ReachEventName, ...ReachEventName[]]

export const reachAudioSchema = z.object({
  cruise: z
    .object({
      speedMin: positive,
      speedMax: positive,
      gainMin: z.number().min(0).max(2),
      gainMax: z.number().min(0).max(2),
      rateMin: z.number().min(0.25).max(4),
      rateMax: z.number().min(0.25).max(4),
      cutoffMin: positive.max(24000),
      cutoffMax: positive.max(24000),
      /** Gain multiplier while riding a trade lane. */
      laneGain: z.number().min(0).max(2),
    })
    .refine((c) => c.speedMax > c.speedMin, { message: 'cruise.speedMax must exceed cruise.speedMin' }),
  events: z.partialRecord(z.enum(eventNames), reachEventSoundSchema),
})

export type ReachAudioConfig = z.infer<typeof reachAudioSchema>
export type CruiseAudioConfig = ReachAudioConfig['cruise']
export type ReachEventSound = z.infer<typeof reachEventSoundSchema>

/** Parses and validates reach audio data; throws with the Zod path on bad data. */
export function parseReachAudioConfig(raw: unknown): ReachAudioConfig {
  return reachAudioSchema.parse(raw)
}

/** The bundled `reach-audio.json`, parsed once. */
export const reachAudioConfig: ReachAudioConfig = parseReachAudioConfig(reachAudioJson)
